import zipcelx from 'zipcelx'

import { filters } from '@app/insights/constants'
import { filterDefinitions } from '@app/insights/filter-definitions'
import { columnLabel } from '@app/insights/utils/column-label'

type ExportColumn = {
  name: string
  title: string
  subtitle?: string
  filter?: { type: string } | null
}

type ExportCell = {
  formatted_value?: string | null
  sort_value?: number | null
  filter_error?: boolean
}

type ExportCompany = {
  id: number
  filter_values: Record<string, ExportCell>
}

export type ExcelExportInput = {
  companyColumns: ExportColumn[]
  sortedAndFilteredCompanies: ExportCompany[]
  selectedCompanies: Set<number>
}

type ExcelCell = {
  value: unknown
  type: string
}

function formatForExport(column: ExportColumn, value: unknown): unknown {
  const columnFilter = column.filter
  if (!columnFilter) {
    return value
  }

  const filterDefinition = filterDefinitions.find(
    (filterType) => filterType.type === columnFilter.type
  ) as { formatForExport?: (value: unknown) => unknown } | undefined

  const exported =
    filterDefinition && filterDefinition.formatForExport
      ? filterDefinition.formatForExport(value)
      : value

  // Mirror the on-screen EmptyCellDash: a blank filter cell exports as "-"
  // rather than an empty cell.
  const isBlank = exported === '' || exported === null || exported === undefined

  return isBlank ? '-' : exported
}

function excelValues(input: ExcelExportInput): ExcelCell[][] {
  const { companyColumns, sortedAndFilteredCompanies, selectedCompanies } =
    input
  const numericFilterTypes: string[] = [filters.FORMULA]

  return sortedAndFilteredCompanies.reduce<ExcelCell[][]>((result, company) => {
    if (!selectedCompanies.has(company.id)) {
      return result
    }

    const rowData = company.filter_values
    const columnWithId: ExcelCell = {
      value: company.id,
      type: 'number',
    }

    const excelCells = companyColumns.map((column): ExcelCell => {
      if (rowData[column.name].filter_error) {
        return {
          value: window.I18n.t('companies_overview.filters.not_applicable'),
          type: 'string',
        }
      }

      if (column.filter && numericFilterTypes.includes(column.filter.type)) {
        return {
          value: rowData[column.name].sort_value,
          type: 'number',
        }
      }

      const value = rowData[column.name].formatted_value
      const valueForExport = formatForExport(column, value)

      return {
        value: valueForExport,
        type: 'string',
      }
    })

    result.push([columnWithId, ...excelCells])
    return result
  }, [])
}

function excelData(input: ExcelExportInput): ExcelCell[][] {
  const headerForId: ExcelCell = {
    value: window.I18n.t('companies_overview.company_id'),
    type: 'string',
  }

  const headers = input.companyColumns.map((column): ExcelCell => {
    return { value: columnLabel(column.title, column.subtitle), type: 'string' }
  })

  return [[headerForId, ...headers], ...excelValues(input)]
}

export function downloadCompaniesExcel(input: ExcelExportInput): void {
  zipcelx({
    filename: 'companies_overview',
    sheet: { data: excelData(input) },
  })
}
